import type {
  CanonicalCompatibilityEvidence,
} from '../tree/CanonicalTreeCompatibilityEdgeProjection';

import type {
  CanonicalTreeTopologyReplacementMemberResult,
} from '../tree/CanonicalTreeTopologyReplacementSafetyGate';

import type {
  CanonicalViewerMemberProjection,
} from '../tree/CanonicalTreeViewerProjection';

export const TREE_RUNTIME_SELECTOR_DISCREPANCY_KEYS =
  Object.freeze([
    'canonicalAdditionSelfCount',
    'canonicalAdditionMaternalCount',
    'canonicalAdditionPaternalCount',
    'canonicalAdditionBothCount',
    'canonicalAdditionDescendantCount',
    'canonicalAdditionPartnerAffiliatedCount',
    'canonicalAdditionChildMediatedCount',
    'canonicalAdditionAffiliatedCount',
    'canonicalAdditionUnresolvedCount',

    'generationMismatchCanonicalMoreAncestralCount',
    'generationMismatchCanonicalMoreDescendantCount',
    'generationMismatchLegacyMissingCount',
    'generationMismatchCanonicalMissingCount',
    'generationMismatchIndeterminateCount',

    'compatibilityMissingReferencedMemberCount',
    'compatibilitySelfReferenceCount',
    'compatibilitySharedChildEvidenceMissingCount',
    'compatibilityUnsupportedRelationshipKindCount',
    'compatibilityWorkflowEvidenceInsufficientCount',
    'compatibilityOtherUnresolvedCount',
    'compatibilityInvalidCanonicalEdgeCount',
  ] as const);

export type TreeRuntimeSelectorDiscrepancyKey =
  typeof TREE_RUNTIME_SELECTOR_DISCREPANCY_KEYS[number];

export type TreeRuntimeSelectorDiscrepancyClassification =
  Readonly<
    Record<
      TreeRuntimeSelectorDiscrepancyKey,
      number
    >
  >;

export interface TreeRuntimeSelectorDiscrepancyInput {
  readonly canonicalProjections:
    ReadonlyMap<
      string,
      CanonicalViewerMemberProjection
    >;

  readonly topologyMembers:
    readonly CanonicalTreeTopologyReplacementMemberResult[];

  readonly unresolvedCompatibilityEvidence:
    readonly CanonicalCompatibilityEvidence[];

  readonly invalidCompatibilityEvidence:
    readonly CanonicalCompatibilityEvidence[];
}

const createMutableCounts =
  (): Record<
    TreeRuntimeSelectorDiscrepancyKey,
    number
  > =>
    Object.fromEntries(
      TREE_RUNTIME_SELECTOR_DISCREPANCY_KEYS.map(
        key => [key, 0],
      ),
    ) as Record<
      TreeRuntimeSelectorDiscrepancyKey,
      number
    >;

const classifyCanonicalAddition =
  (
    projection:
      CanonicalViewerMemberProjection | undefined,
    counts:
      Record<
        TreeRuntimeSelectorDiscrepancyKey,
        number
      >,
  ): void => {
    switch (projection?.branch) {
      case 'self':
        counts.canonicalAdditionSelfCount += 1;
        return;

      case 'maternal':
        counts.canonicalAdditionMaternalCount += 1;
        return;

      case 'paternal':
        counts.canonicalAdditionPaternalCount += 1;
        return;

      case 'both':
        counts.canonicalAdditionBothCount += 1;
        return;

      case 'descendant':
        counts.canonicalAdditionDescendantCount += 1;
        return;

      case 'partner-affiliated':
        counts
          .canonicalAdditionPartnerAffiliatedCount += 1;
        return;

      case 'child-mediated':
        counts
          .canonicalAdditionChildMediatedCount += 1;
        return;

      case 'affiliated':
        counts.canonicalAdditionAffiliatedCount += 1;
        return;

      case 'unresolved':
      default:
        counts.canonicalAdditionUnresolvedCount += 1;
    }
  };

const classifyGenerationMismatch =
  (
    result:
      CanonicalTreeTopologyReplacementMemberResult,
    counts:
      Record<
        TreeRuntimeSelectorDiscrepancyKey,
        number
      >,
  ): void => {
    const legacy =
      result.legacyGeneration;

    const canonical =
      result.candidateGeneration;

    if (legacy === null) {
      counts
        .generationMismatchLegacyMissingCount += 1;
      return;
    }

    if (canonical === null) {
      counts
        .generationMismatchCanonicalMissingCount += 1;
      return;
    }

    if (canonical > legacy) {
      counts
        .generationMismatchCanonicalMoreAncestralCount += 1;
      return;
    }

    if (canonical < legacy) {
      counts
        .generationMismatchCanonicalMoreDescendantCount += 1;
      return;
    }

    counts
      .generationMismatchIndeterminateCount += 1;
  };

const classifyUnresolvedCompatibility =
  (
    evidence:
      CanonicalCompatibilityEvidence,
    counts:
      Record<
        TreeRuntimeSelectorDiscrepancyKey,
        number
      >,
  ): void => {
    const reason =
      evidence.reason
        .trim()
        .toLowerCase();

    if (
      reason.includes(
        'not present in the loaded graph',
      )
    ) {
      counts
        .compatibilityMissingReferencedMemberCount += 1;
      return;
    }

    if (
      reason.includes(
        'self-reference',
      )
    ) {
      counts
        .compatibilitySelfReferenceCount += 1;
      return;
    }

    if (
      reason.includes(
        'shared-child connector',
      )
    ) {
      counts
        .compatibilitySharedChildEvidenceMissingCount += 1;
      return;
    }

    if (
      reason.includes(
        'not recognized by the canonical edge contract',
      )
    ) {
      counts
        .compatibilityUnsupportedRelationshipKindCount += 1;
      return;
    }

    if (
      reason.includes(
        'did not contain enough unique canonical evidence',
      )
    ) {
      counts
        .compatibilityWorkflowEvidenceInsufficientCount += 1;
      return;
    }

    counts
      .compatibilityOtherUnresolvedCount += 1;
  };

export const classifyTreeRuntimeSelectorDiscrepancies =
  (
    input:
      TreeRuntimeSelectorDiscrepancyInput,
  ): TreeRuntimeSelectorDiscrepancyClassification => {
    const counts =
      createMutableCounts();

    for (
      const result of
      input.topologyMembers
    ) {
      if (
        result.classification ===
        'CANONICAL-ONLY'
      ) {
        classifyCanonicalAddition(
          input.canonicalProjections.get(
            result.memberId,
          ),
          counts,
        );
      }

      if (
        result.classification ===
        'GENERATION-MISMATCH'
      ) {
        classifyGenerationMismatch(
          result,
          counts,
        );
      }
    }

    for (
      const evidence of
      input.unresolvedCompatibilityEvidence
    ) {
      classifyUnresolvedCompatibility(
        evidence,
        counts,
      );
    }

    counts.compatibilityInvalidCanonicalEdgeCount =
      input.invalidCompatibilityEvidence.length;

    return Object.freeze(counts);
  };

export const sanitizeTreeRuntimeSelectorDiscrepancyClassification =
  (
    candidate: unknown,
  ): TreeRuntimeSelectorDiscrepancyClassification | null => {
    if (
      !candidate ||
      typeof candidate !== 'object'
    ) {
      return null;
    }

    const value =
      candidate as Record<string, unknown>;

    const sanitized =
      createMutableCounts();

    for (
      const key of
      TREE_RUNTIME_SELECTOR_DISCREPANCY_KEYS
    ) {
      const count =
        value[key];

      if (
        typeof count !== 'number' ||
        !Number.isInteger(count) ||
        count < 0 ||
        count > 1_000_000
      ) {
        return null;
      }

      sanitized[key] = count;
    }

    return Object.freeze(sanitized);
  };

export const createEmptyTreeRuntimeSelectorDiscrepancyClassification =
  (): TreeRuntimeSelectorDiscrepancyClassification =>
    Object.freeze(
      createMutableCounts(),
    );
